import { useState, useEffect, useCallback } from 'react'
import { Helmet } from 'react-helmet-async'
import { getBandListings, type BandListingResponse } from '../api/bandListings'
import { telHref } from '../lib/phone'
import type { BandListing } from '../types'

const PAGE_SIZE = 50

/**
 * The AU Band List.
 *
 * It was a members-only page on the old pigeon.org, sitting in the same
 * navigation as the Member Directory, and it did not survive the move to Wix.
 * Lauren Gandara, 2026-09-14: "the Band Listings are not on the dashboard. It
 * was previously paired with the Member Directory."
 *
 * Same eight columns and same search the old page had. The phone number is
 * dialable here, which it was not there: this is the page a member opens to
 * find whoever owns a band they are holding.
 */
export function BandListings() {
  const [listings, setListings] = useState<BandListing[]>([])
  const [seasons, setSeasons] = useState<number[]>([])
  // Two seasons, on purpose. requestedSeason is what the fetch asks for and is
  // null until a member picks one; season is what the server actually served.
  // Keying the fetch on the served value instead would fetch twice on load,
  // once with null and again the moment the answer came back.
  const [requestedSeason, setRequestedSeason] = useState<number | null>(null)
  const [season, setSeason] = useState<number | null>(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  const [search, setSearch] = useState('')
  const [searchInput, setSearchInput] = useState('')
  const [page, setPage] = useState(1)
  const [totalPages, setTotalPages] = useState(1)
  const [totalRows, setTotalRows] = useState(0)

  const fetchData = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const result: BandListingResponse = await getBandListings({
        season: requestedSeason ?? undefined,
        search: search || undefined,
        page,
        limit: PAGE_SIZE,
      })
      setListings(result.listings)
      setSeasons(result.seasons)
      // The server decides the season when none was asked for, and when an
      // unknown one was. Taking it back keeps the dropdown honest.
      setSeason(result.season)
      setTotalPages(result.totalPages)
      setTotalRows(result.total)
    } catch {
      setError('Unable to load the band list. Please try again.')
    } finally {
      setLoading(false)
    }
  }, [requestedSeason, search, page])

  useEffect(() => {
    fetchData()
  }, [fetchData])

  function handleSearch(e: React.FormEvent) {
    e.preventDefault()
    setSearch(searchInput)
    setPage(1)
  }

  function handleSeasonChange(e: React.ChangeEvent<HTMLSelectElement>) {
    setRequestedSeason(Number(e.target.value))
    setPage(1)
  }

  function handleClear() {
    setSearch('')
    setSearchInput('')
    setPage(1)
  }

  return (
    <div>
      <Helmet>
        <title>Band Listings - ARPU</title>
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <h1 className="text-2xl font-heading font-bold text-text mb-2">Band Listings</h1>
      <p className="text-text-light mb-6">
        Which club or member a band series belongs to. Search by band code, name, club, city or state.
      </p>

      <div className="flex flex-wrap gap-3 items-end mb-4">
        <form onSubmit={handleSearch} className="flex gap-2 flex-1 min-w-[200px]">
          <label htmlFor="band-search" className="sr-only">Search the band list</label>
          <input
            id="band-search"
            type="text"
            value={searchInput}
            onChange={(e) => setSearchInput(e.target.value)}
            placeholder="Search by band code, name, club, city..."
            className="flex-1 max-w-sm px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50 focus:border-primary"
          />
          <button
            type="submit"
            className="px-4 py-2 bg-primary text-text-inverse text-sm rounded-md hover:bg-primary-light transition-colors"
          >
            Search
          </button>
        </form>

        <label htmlFor="band-season" className="sr-only">Band year</label>
        <select
          id="band-season"
          value={season ?? ''}
          onChange={handleSeasonChange}
          className="px-3 py-2 border border-border rounded-md bg-surface text-text text-sm focus:outline-none focus:ring-2 focus:ring-primary/50"
        >
          {seasons.map((s) => (
            <option key={s} value={s}>{s}</option>
          ))}
        </select>

        {search && (
          <button
            onClick={handleClear}
            className="px-3 py-2 text-sm text-text-muted hover:text-text border border-border rounded-md hover:bg-surface-hover transition-colors"
          >
            Clear
          </button>
        )}
      </div>

      <p className="text-sm text-text-muted mb-3">
        {totalRows} record{totalRows !== 1 ? 's' : ''} found{season ? ` for ${season}` : ''}
      </p>

      {error && (
        <div className="bg-error/10 border border-error/30 text-error rounded-md px-4 py-3 text-sm mb-4">
          {error}
        </div>
      )}

      {/* A phone reads the cards; anything wider reads the table. */}
      <div className="sm:hidden space-y-3">
        {loading ? (
          <p className="text-center py-8 text-text-muted">Loading...</p>
        ) : listings.length === 0 ? (
          <p className="text-center py-8 text-text-muted">No band orders found matching your search.</p>
        ) : (
          listings.map((b) => (
            <div key={`${b.fullCode}-${b.characterCode}-${b.personName}`} className="border border-border rounded-lg p-4 bg-surface">
              <p className="font-heading font-semibold text-text">{b.fullCode || b.characterCode}</p>
              {b.orgName && <p className="text-sm text-text-light">{b.orgName}</p>}
              {b.personName && <p className="text-sm text-text-muted">{b.personName}</p>}
              {(b.city || b.state) && (
                <p className="text-sm text-text-muted">{[b.city, b.state].filter(Boolean).join(', ')}</p>
              )}
              <PhoneCell phone={b.phone} className="text-sm text-text-muted" />
            </div>
          ))
        )}
      </div>

      <div className="hidden sm:block overflow-x-auto border border-border rounded-lg">
        <table className="w-full text-sm">
          <thead>
            <tr className="bg-primary text-text-inverse">
              {['Season', 'Char Code', 'Full Code', 'Org Name', 'Person Name', 'City', 'State', 'Phone'].map((h) => (
                <th key={h} className="px-4 py-3 text-left font-medium text-xs uppercase tracking-wider">{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-text-muted">
                  <div className="flex items-center justify-center">
                    <div className="animate-spin rounded-full h-5 w-5 border-2 border-primary border-t-transparent mr-2" />
                    Loading...
                  </div>
                </td>
              </tr>
            ) : listings.length === 0 ? (
              <tr>
                <td colSpan={8} className="px-4 py-8 text-center text-text-muted">
                  No band orders found matching your search.
                </td>
              </tr>
            ) : (
              listings.map((b) => (
                <tr
                  key={`${b.fullCode}-${b.characterCode}-${b.personName}`}
                  className="border-t border-border hover:bg-surface-hover transition-colors"
                >
                  <td className="px-4 py-2.5">{b.season}</td>
                  <td className="px-4 py-2.5">{b.characterCode}</td>
                  <td className="px-4 py-2.5">{b.fullCode}</td>
                  <td className="px-4 py-2.5">{b.orgName}</td>
                  <td className="px-4 py-2.5">{b.personName}</td>
                  <td className="px-4 py-2.5">{b.city}</td>
                  <td className="px-4 py-2.5">{b.state}</td>
                  <td className="px-4 py-2.5 whitespace-nowrap">
                    <PhoneCell phone={b.phone} className="" />
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {totalPages > 1 && (
        <div className="flex items-center justify-between mt-4">
          <p className="text-sm text-text-muted">Page {page} of {totalPages}</p>
          <div className="flex gap-2">
            <button
              onClick={() => setPage(1)}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              First
            </button>
            <button
              onClick={() => setPage((p) => Math.max(1, p - 1))}
              disabled={page === 1}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Previous
            </button>
            <button
              onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Next
            </button>
            <button
              onClick={() => setPage(totalPages)}
              disabled={page === totalPages}
              className="px-3 py-1.5 text-sm border border-border rounded-md hover:bg-surface-hover disabled:opacity-40 disabled:cursor-not-allowed transition-colors"
            >
              Last
            </button>
          </div>
        </div>
      )}
    </div>
  )
}

/** The phone, dialable when there is something to dial and plain text when not. */
function PhoneCell({ phone, className }: { phone: string; className: string }) {
  if (!phone) return null
  const href = telHref(phone)
  if (href === '') {
    return <span className={className}>{phone}</span>
  }
  return (
    <a href={href} className={`${className} text-primary hover:underline`}>{phone}</a>
  )
}
